import React, { useState } from 'react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { listAttachments } from '../../api/chat';
import { chatKeys } from '../../hooks/chat/chatKeys';
import RoomSectionHeader from './RoomSectionHeader';
import FileTypeIcon from './FileTypeIcon';
import type { ChatAttachment } from '../../types/chat';
import { parseApiDate } from '../../utils/apiDates';

interface AttachmentsAccordionProps {
  roomId: string;
  defaultOpen?: boolean;
}

function isImage(att: ChatAttachment): boolean {
  if (att.mime_type?.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(att.original_name ?? '');
}

const AttachmentsAccordion: React.FC<AttachmentsAccordionProps> = ({
  roomId,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const { data } = useQuery({
    queryKey: chatKeys.attachments(roomId),
    queryFn: () => listAttachments(roomId),
    enabled: !!roomId && open,
    staleTime: 60_000,
  });
  const items = data ?? [];

  return (
    <section>
      <RoomSectionHeader
        label="Attachments"
        count={items.length || undefined}
        open={open}
        onToggle={() => setOpen((p) => !p)}
      />
      {open && (
        <ul className="px-4 py-5 space-y-4">
          {items.length === 0 ? (
            <li className="text-xs text-text-tertiary text-center py-4">
              No attachments yet.
            </li>
          ) : (
            items.map((a) => (
              <li key={a.id}>
                <a
                  href={a.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 group"
                >
                  {isImage(a) && a.url ? (
                    <img
                      src={a.url}
                      alt={a.original_name}
                      className="shrink-0 w-10 h-10 rounded-lg object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <FileTypeIcon mime={a.mime_type} filename={a.original_name} />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-text-primary truncate group-hover:text-primary-600 dark:group-hover:text-primary-300">
                      {a.original_name}
                    </p>
                    {a.created_at && (
                      <p className="text-[12px] text-text-tertiary">
                        {format(parseApiDate(a.created_at), 'dd MMM yyyy h:mm a')}
                      </p>
                    )}
                  </div>
                </a>
              </li>
            ))
          )}
        </ul>
      )}
    </section>
  );
};

export default AttachmentsAccordion;
